import React, { createContext, useContext, useState, useEffect } from 'react';
import type { AgoraParticipantLike, AgoraRoomLike } from '../../../types';
import { sameAgoraParticipant } from '../../../utils/sameAgoraParticipant';
import { useAppState } from '../../../state';

type selectedParticipantContextType = [
  AgoraParticipantLike | null,
  (participant: AgoraParticipantLike) => void,
];

export const selectedParticipantContext = createContext<selectedParticipantContextType>(null!);

export default function useSelectedParticipant() {
  const [selectedParticipant, setSelectedParticipant] = useContext(selectedParticipantContext);
  return [selectedParticipant, setSelectedParticipant] as const;
}

type SelectedParticipantProviderProps = {
  room: AgoraRoomLike | null;
  children: React.ReactNode;
};

export function SelectedParticipantProvider({ room, children }: SelectedParticipantProviderProps) {
  const { isGalleryViewActive } = useAppState();
  const [selectedParticipant, _setSelectedParticipant] = useState<AgoraParticipantLike | null>(null);
  const setSelectedParticipant = (participant: AgoraParticipantLike) =>
    _setSelectedParticipant(prev => (prev && sameAgoraParticipant(prev, participant) ? null : participant));

  useEffect(() => {
    if (isGalleryViewActive) _setSelectedParticipant(null);
  }, [isGalleryViewActive]);

  useEffect(() => {
    if (!room) return;
    const client = room.client;
    const onLeave = () => _setSelectedParticipant(null);
    const onUserLeft = (user: { uid: number }) => {
      _setSelectedParticipant(prev => (prev && prev.uid === user.uid ? null : prev));
    };
    const onStateChange = (state: string) => {
      if (state === 'DISCONNECTED') onLeave();
    };
    client.on('connection-state-change', onStateChange);
    client.on('user-left', onUserLeft);
    return () => {
      client.off('connection-state-change', onStateChange);
      client.off('user-left', onUserLeft);
    };
  }, [room]);

  useEffect(() => {
    if (!room) return;
    _setSelectedParticipant(prev => {
      if (!prev) return prev;
      const stillRemote = (room.client.remoteUsers ?? []).some(user => Number(user.uid) === Number(prev.uid));
      return stillRemote ? prev : null;
    });
  }, [room, room?.client?.remoteUsers?.length]);

  return (
    <selectedParticipantContext.Provider value={[selectedParticipant, setSelectedParticipant]}>
      {children}
    </selectedParticipantContext.Provider>
  );
}
